/**
 * Device Simulator — replay synthetic observations
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Bot, Play, Square, Activity, Cpu, AlertTriangle } from "lucide-react";
import { getMockProfiles, STATUS_TONE, STATUS_LABEL } from "@/lib/device/profiles";

export const Route = createFileRoute("/integration/device-simulator")({
  component: DeviceSimulator,
});

function DeviceSimulator() {
  const qc = useQueryClient();
  const [running, setRunning] = useState<string | null>(null);
  const [rate, setRate] = useState([10]); // events per minute

  const profilesQ = useQuery({
    queryKey: ["device-profiles"],
    queryFn: async () => {
      await new Promise((r) => setTimeout(r, 150));
      return getMockProfiles();
    },
  });

  const simStateQ = useQuery({
    queryKey: ["device-simulator-state", running],
    queryFn: async () => {
      if (!running) return { events: [] as { id: string; at: number; obs: string; result: string }[] };
      // Simulate adding events at "rate" per minute
      const now = Date.now();
      const count = Math.min(50, Math.floor(rate[0]));
      return {
        events: Array.from({ length: count }).map((_, i) => ({
          id: `sim-${i}`,
          at: now - i * (60_000 / rate[0]),
          obs: ["HbA1c", "Glucose", "SpO2", "Heart rate", "BP"][i % 5],
          result: i % 11 === 0 ? "ERROR" : i % 5 === 0 ? "WARNING" : "SUCCESS",
        })),
      };
    },
    refetchInterval: running ? 2000 : false,
  });

  function startSim(profileId: string) {
    setRunning(profileId);
    qc.invalidateQueries({ queryKey: ["device-simulator-state"] });
  }

  function stopSim() {
    setRunning(null);
  }

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Bot className="h-5 w-5" /> Device Simulator
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Replay d'observations synthétiques · Test d'intégration · Validation pipeline
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Configuration de la simulation</CardTitle>
          <CardDescription>Choisissez un profil + fréquence d'émission</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">Fréquence</label>
              <span className="text-sm font-mono">{rate[0]} obs/min</span>
            </div>
            <Slider value={rate} onValueChange={setRate} min={1} max={120} step={1} className="w-full" />
          </div>

          {running && (
            <div className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
              <Activity className="h-4 w-4 text-emerald-400 animate-pulse" />
              <div className="flex-1 text-sm">
                Simulation active · {simStateQ.data?.events.length ?? 0} événements émis
              </div>
              <Button size="sm" variant="outline" onClick={stopSim}>
                <Square className="h-3.5 w-3.5 mr-1" /> Arrêter
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Profils disponibles pour simulation</CardTitle>
          <CardDescription>Cliquez pour démarrer la simulation</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
            {(profilesQ.data ?? []).map((p) => (
              <div key={p.id} className="flex items-center gap-2 rounded-md border border-white/5 bg-white/[0.02] p-2 text-sm">
                <Cpu className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="flex-1 font-medium">{p.vendor} {p.model}</span>
                <Badge variant={STATUS_TONE[p.status]} className="text-[10px]">{STATUS_LABEL[p.status]}</Badge>
                <Button
                  size="sm"
                  variant={running === p.id ? "destructive" : "outline"}
                  onClick={() => running === p.id ? stopSim() : startSim(p.id)}
                  disabled={p.status === "OFFLINE" && running !== p.id}
                >
                  {running === p.id ? <Square className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {simStateQ.data && simStateQ.data.events.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Stream d'événements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 max-h-64 overflow-y-auto">
              {simStateQ.data.events.map((e) => (
                <li key={e.id} className="flex items-center gap-2 rounded-md border border-white/5 bg-white/[0.02] p-1.5 text-xs">
                  <span className="text-muted-foreground font-mono w-20">
                    {new Date(e.at).toLocaleTimeString("fr-FR")}
                  </span>
                  <span className="font-medium">{e.obs}</span>
                  <span className="ml-auto">
                    {e.result === "SUCCESS" ? <Badge variant="secondary" className="text-[9px]">OK</Badge> :
                      e.result === "WARNING" ? <Badge variant="outline" className="text-[9px]">WARN</Badge> :
                        <Badge variant="destructive" className="text-[9px]"><AlertTriangle className="h-2 w-2 mr-0.5" />ERR</Badge>}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
